import { clients } from 'lib/https';
import { getConfigurationSettings } from 'services/configuration-service';
import type {
  IAgentListResponse,
  IGetAgentsActivityOptions,
} from '../types/agent.service.type';

export type Agent = {
  id: string;
  created_date: string;
  last_updated_date: string;
  created_by: string;
  language: string;
  last_updated_by: string;
  organization_ids: string[];
  tags: string[];
  name: string;
  agent_type: string | null;
  role: string;
  description: string;
  domain_expertise: string[];
  enable_rag: boolean;
  logo_url: string | null;
  logo_id: string | null;
  is_disabled: boolean;
  is_archived: boolean;
  enable_tools: boolean;
  tool_ids: string[];
  enable_knowledge_base: boolean;
  enable_memory: boolean;
  enable_human_handoff: boolean;
  published_date?: string;
  response_type: string;
  response_format: string | null;
  widget_id: string;
};

export type AgentSummary = Pick<
  Agent,
  | 'id'
  | 'name'
  | 'tags'
  | 'role'
  | 'description'
  | 'logo_url'
  | 'logo_id'
  | 'is_archived'
  | 'is_disabled'
  | 'tool_ids'
  | 'widget_id'
>;

export type IGetAgentsModelPayload = IGetAgentsActivityOptions & {
  limit: number;
  offset: number;
  project_key: string;
};

export type IGetAgentsResponse = IAgentListResponse<AgentSummary>;

export type IGetAgentModelResponse = {
  id?: string;
  is_system?: boolean;
  is_active?: boolean;
  status?: string;
  provider: string;
  model_name: string;
  model_name_label: string;
  provider_label: string;
  model_type: 'chat' | 'embedding';
  region?: string;
  max_tokens?: number | null;
  default_temp?: number | null;
  disallowed?: boolean;
  disallowed_reason?: string;
}[];

export type ToolType = 'api' | 'mcp_server' | 'webhook' | 'graphql' | 'composio' | 'open_connector';
export type ToolStatus = 'active' | 'inactive' | 'deprecated' | 'maintenance';

export interface IGetToolsPayload {
  tool_type?: ToolType | null;
  tool_status?: ToolStatus | null;
  page?: number;
  page_size?: number;
  project_key?: string;
  search?: string;
  /** Catalog connectors (Featured). */
  created_by?: string;
  /** Exclude system tools (My Tools). */
  with_system_tool?: boolean;
}

export interface ITool {
  id: string;
  name: string;
  type: ToolType;
  status: ToolStatus;
  description: string;
  total_uses: number;
  total_actions: number;
  is_open_tool: boolean;
  visibility?: 'private' | 'public';
  /** Owner user id — snake_case from tools API (same role as Agent.created_by). */
  created_by?: string;
  /** Owner user id — PascalCase variant some tool payloads still return. */
  CreatedBy?: string;
  /** Server-computed write access (owner or org admin). List payload. */
  can_edit?: boolean | null;
  /** Server-computed write access (owner or org admin). Detail payload. */
  CanEdit?: boolean | null;
  ComposioConfig?: IToolComposioConfig;
  composio_config?: IToolComposioConfig;
  OpenConnectorConfig?: IToolOpenConnectorConfig;
  open_connector_config?: IToolOpenConnectorConfig;
  /** Provider slugs from the tools list, for example ["slack"]. */
  providers?: string[];
}

export type ToolAuthType = 'none' | 'bearer' | 'basic' | 'api_token' | 'oauth2' | 'auto';
export type ToolAuthLocation = 'header' | 'query';
export type ToolAutoSubType = 'password' | 'oidc' | 'host';

export interface IToolOAuth2Config {
  ClientID?: string;
  ClientSecret?: string;
  TokenURL?: string;
  AuthorizationEndpoint?: string;
  Scopes?: string[];
  GrantType?: string;
}

export interface IToolAuthConfig {
  AuthType?: ToolAuthType;
  AuthKeyLocation?: ToolAuthLocation;
  AuthKeyName?: string;
  AuthKeyPrefix?: string;
  APIToken?: string;
  Username?: string;
  Password?: string;
  OAuth2Config?: IToolOAuth2Config;
  CustomHeaders?: Record<string, string>;
  /** v2 "auto" (per-user, token-only) config — the tool detail returns these PascalCase keys. */
  AutoSubType?: ToolAutoSubType;
  LoginUrl?: string;
  LoginMethod?: string;
  UsernameField?: string;
  PasswordField?: string;
  TokenJsonPath?: string;
  ExpiresJsonPath?: string;
  RefreshUrl?: string;
  RefreshTokenJsonPath?: string;
  RefreshRequestField?: string;
  /** v2 "auto" config — snake_case keys accepted by the save endpoint. */
  auth_type?: ToolAuthType;
  auto_sub_type?: ToolAutoSubType;
  auth_key_name?: string;
  auth_key_prefix?: string;
  login_url?: string;
  login_method?: string;
  username_field?: string;
  password_field?: string;
  token_json_path?: string;
  refresh_url?: string;
  refresh_token_json_path?: string;
  refresh_request_field?: string;
  oauth2_config?: {
    authorization_endpoint?: string;
    /** BE expects `token_url` (alias TokenURL), NOT token_endpoint. */
    token_url?: string;
    TokenURL?: string;
    client_id?: string;
    client_secret?: string;
    scopes?: string[];
    grant_type?: string;
  };
}

/** Async Swagger-ingest report persisted on the tool (v2). Populated only once the worker finishes. */
export interface IToolIngestReport {
  /** Terminal states: "ready" | "needs_auth_confirmation" | "failed". */
  status?: string;
  found?: number;
  converted?: number;
  /** May be a count or a list of skipped operations depending on the BE. */
  skipped?: number | Array<{ op?: string; reason?: string }>;
  error?: string;
  specUrl?: string;
  specHash?: string;
  specVersion?: string;
  authConfirmed?: boolean;
  spec_source?: string;
  spec_hash?: string;
}

/** 'GRAPHQL' flags an action as a GraphQL operation on the tool's single endpoint. */
export type ToolApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'GRAPHQL' | string;
export type GraphQLOperationType = 'query' | 'mutation';

export interface IToolApiAction {
  Method?: ToolApiMethod;
  Name?: string;
  Description?: string;
  Endpoint?: string;
  InputParameters?: Record<string, unknown>;
  OutputParameters?: Record<string, unknown>;
  TestStatus?: boolean;
  CreatedAt?: string;
  /** v2: per-operation active/inactive flag (reads on, writes off by default). */
  Enabled?: boolean;
  enabled?: boolean;
  /** true forces human approval for this action; false/absent inherits the tool setting. */
  RequiresApproval?: boolean;
  /** GraphQL flavor (Method === 'GRAPHQL'): the operation text and its kind. */
  GraphQLQuery?: string;
  GraphQLOperationType?: GraphQLOperationType;
}

export interface IToolApiConfig {
  BaseURL?: string;
  base_url?: string;
  Headers?: Record<string, string>;
  AuthConfig?: IToolAuthConfig;
  Actions?: Record<string, IToolApiAction>;
}

export interface IToolParameterDefinition {
  Name?: string;
  Description?: string;
  Type?: string;
  Required?: boolean;
  DefaultValue?: unknown;
}

export type MCPAuthMode = 'none' | 'static' | 'client_credentials' | 'per_user';
export type MCPProbeStatus = 'none' | 'per_user' | 'manual_client_required' | 'failed';

export interface IToolMcpAuthConfig {
  Enabled?: boolean;
  APIToken?: string;
  APIKeyPrefix?: string;
  APIKeyName?: string;
  OAuth2Config?: {
    ClientID?: string;
    ClientSecret?: string;
    TokenURL?: string;
    AuthorizationEndpoint?: string;
    RedirectURI?: string;
    Scopes?: string[];
    GrantType?: string;
    AccessToken?: string;
  };
  /** OAuth 2.1 per-user (the MCP standard); filled in by the save-time probe. */
  AuthMode?: MCPAuthMode | null;
  RegisteredClientID?: string | null;
  RegistrationClientURI?: string | null;
  AuthorizationEndpoint?: string | null;
  TokenEndpoint?: string | null;
  Resource?: string | null;
  Scopes?: string[] | null;
  ProbeStatus?: MCPProbeStatus | null;
  ProbeDetail?: string | null;
  NeedsManualClient?: boolean;
  /** Host session: replay the embedding site's visitor token. */
  AcceptHostToken?: boolean;
  TokenAsCookie?: boolean;
  TokenCookieName?: string | null;
}

export interface IToolMcpAction {
  Name?: string;
  Description?: string;
  Method?: string;
  Endpoint?: string;
  InputParameters?: Record<string, IToolParameterDefinition>;
  OutputParameters?: Record<string, IToolParameterDefinition>;
}

export interface IToolMcpConfig {
  ServerURL?: string;
  Transport?: 'sse' | 'streamable_http' | string;
  TrustMCPServer?: boolean;
  Headers?: Record<string, string>;
  QueryParams?: Record<string, string>;
  AuthConfig?: IToolMcpAuthConfig;
  Actions?: Record<string, IToolMcpAction>;
}

export interface IToolComposioConfig {
  Toolkits?: string[];
  toolkits?: string[];
  Tools?: string[] | null;
  tools?: string[] | null;
  Limit?: number | null;
  limit?: number | null;
  AuthConfigIds?: Record<string, string> | null;
  auth_config_ids?: Record<string, string> | null;
}

export interface IToolOpenConnectorConfig {
  Services?: string[];
  services?: string[];
  Actions?: string[] | null;
  actions?: string[] | null;
  Limit?: number | null;
  limit?: number | null;
}

export interface IToolDetails extends ITool {
  tutorial?: string;
  Tutorial?: string;
  base_url?: string;
  APIConfig?: IToolApiConfig;
  api_config?: IToolApiConfig;
  MCPConfig?: IToolMcpConfig;
  ComposioConfig?: IToolComposioConfig;
  composio_config?: IToolComposioConfig;
  OpenConnectorConfig?: IToolOpenConnectorConfig;
  open_connector_config?: IToolOpenConnectorConfig;
  IsOpenTool?: boolean;
  IsBlocksAuthenticated?: boolean;
  /** HIL override: null = auto-classify by method, true = always ask, false = never. */
  RequiresApproval?: boolean | null;
  Visibility?: 'private' | 'public';
  created_date?: string;
  CreatedDate?: string;
  last_updated_date?: string;
  LastUpdatedDate?: string;
  /** v2 Swagger-ingest report (snake_case from BE). */
  ingest_report?: IToolIngestReport;
  IngestReport?: IToolIngestReport;
}

export interface IGetToolResponse {
  tool?: IToolDetails;
  data?: IToolDetails;
  _id?: string;
  id?: string;
  Name?: string;
  name?: string;
  Type?: ToolType;
  type?: ToolType;
  Status?: ToolStatus;
  status?: ToolStatus;
  Description?: string;
  description?: string;
  TotalUses?: number;
  total_uses?: number;
  TotalActions?: number;
  total_actions?: number;
  IsOpenTool?: boolean;
  is_open_tool?: boolean;
  RequiresApproval?: boolean | null;
  APIConfig?: IToolApiConfig;
  api_config?: IToolApiConfig;
  base_url?: string;
  MCPConfig?: IToolMcpConfig;
  ComposioConfig?: IToolComposioConfig;
  composio_config?: IToolComposioConfig;
  OpenConnectorConfig?: IToolOpenConnectorConfig;
  open_connector_config?: IToolOpenConnectorConfig;
  Tutorial?: string;
  tutorial?: string;
  IsBlocksAuthenticated?: boolean;
  Visibility?: 'private' | 'public';
  visibility?: 'private' | 'public';
  CreatedBy?: string;
  created_by?: string;
  CanEdit?: boolean | null;
  can_edit?: boolean | null;
  CreatedDate?: string;
  created_date?: string;
  LastUpdatedDate?: string;
  last_updated_date?: string;
  ingest_report?: IToolIngestReport;
  IngestReport?: IToolIngestReport;
}

export interface IGetToolSchemaResponse {
  tool_id: string;
  schema_type: string;
  schema: Record<string, unknown>;
  generated_at: string;
  actions_count: number;
  project_key: string;
}

export interface IUpdateAPIToolPayload {
  id: string;
  name: string;
  description: string;
  base_url: string;
  project_key: string;
  is_open_tool?: boolean;
  is_blocks_authenticated?: boolean;
  status?: ToolStatus;
  visibility?: 'private' | 'public';
  /** HIL override: null = auto-classify by method, true = always ask, false = never. */
  requires_approval?: boolean | null;
}

export interface IUpdateMCPServerPayload {
  id: string;
  name: string;
  description: string;
  project_key: string;
  visibility?: 'private' | 'public';
  is_open_tool?: boolean;
  is_blocks_authenticated?: boolean;
  status?: ToolStatus;
  /** HIL override: null = auto-classify, true = always ask, false = never. */
  requires_approval?: boolean | null;
}

export interface IKeyValueEntry {
  key: string;
  value: string;
}

export interface IAuthConfigPayload {
  AuthType?: ToolAuthType;
  AuthKeyLocation?: ToolAuthLocation;
  AuthKeyName?: string;
  AuthKeyPrefix?: string;
  APIToken?: string;
  Username?: string;
  Password?: string;
  OAuth2Config?: {
    ClientID?: string;
    ClientSecret?: string;
    TokenURL?: string;
    Scopes?: string[];
    GrantType?: string;
  };
  CustomHeaders?: Record<string, string>;
}

export interface ISaveAuthConfigPayload {
  auth_config: IAuthConfigPayload;
  headers?: Record<string, string>;
  project_key?: string;
  is_open_tool?: boolean;
}

export interface ISaveTutorialPayload {
  tutorial: string;
  Tutorial?: string;
  id?: string;
  tool_id?: string;
  is_open_tool?: boolean;
}

export type IGetToolTutorialResponse = IGetToolResponse | {
  tutorial?: string;
  Tutorial?: string;
  data?: { tutorial?: string; Tutorial?: string };
};

export type ConfigParamsAuthType = "none" | "bearer" | "basic" | "api_token" | "oauth2" | "oauth2_client_credentials" | "oauth2_per_user" | "host_session" | "custom";
export type ConfigParamsTransportMethod = "sse" | "streamable_http";

export interface IConfigParamsFormValues {
  transportMethod: ConfigParamsTransportMethod;
  serverUrl: string;
  authType: ConfigParamsAuthType;
  trustMcpServer: boolean;
  authLocation?: "header" | "query";
  authKeyName?: string;
  authKeyPrefix?: string;
  apiToken?: string;
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
  tokenUrl?: string;
  scopes?: string;
  grantType?: "authorization_code" | "client_credentials" | "refresh_token";
  /** Per-user OAuth 2.1: manual client, used only when the server has no dynamic registration. */
  clientIdPerUser?: string;
  clientSecretPerUser?: string;
  /** Host session. */
  tokenAsCookie?: boolean;
  tokenCookieName?: string;
  headers: Array<{ key: string; value: string }>;
  queryParams: Array<{ key: string; value: string }>;
}

export interface IMCPAuthConfigPayload {
  Enabled: boolean;
  AutoDiscoverMetadata: boolean;
  APIToken?: string;
  APIKeyPrefix?: string;
  APIKeyName?: string;
  OAuth2Config?: {
    ClientID: string;
    ClientSecret: string;
    TokenURL: string;
    Scopes: string[];
    GrantType: "client_credentials" | "authorization_code";
    AuthorizationEndpoint?: string;
    RedirectURI?: string;
  };
  AuthMode?: MCPAuthMode;
  AcceptHostToken?: boolean;
  TokenAsCookie?: boolean;
  TokenCookieName?: string;
}

export interface IMCPServerSetupPayload {
  server_url: string;
  transport: string;
  trust_mcp_server: boolean;
  headers?: Record<string, string>;
  query_params?: Record<string, string>;
  auth_config?: IMCPAuthConfigPayload;
  project_key?: string;
  is_open_tool?: boolean;
}

/** Save-time probe outcome: bare request → WWW-Authenticate → RFC 9728/8414/7591. */
export interface IMCPAuthProbeResult {
  status: MCPProbeStatus;
  detail?: string | null;
  needsManualClient?: boolean;
  authorizationEndpoint?: string | null;
  tokenEndpoint?: string | null;
  registered?: boolean;
}

export interface IMCPServerSetupResponse {
  is_success: boolean;
  item_id?: string;
  detail?: string;
  message?: string;
  data?: { mcpProbe?: IMCPAuthProbeResult } | null;
}

export interface ICreateApiActionPayload {
  name: string;
  description: string;
  method: string;
  endpoint: string;
  input_parameters: Record<string, unknown>;
  output_parameters: Record<string, unknown>;
  project_key?: string;
  is_open_tool?: boolean;
  /** v2: per-operation active/inactive toggle. */
  enabled?: boolean;
  /** true forces human approval for this action; false inherits the tool setting. */
  requires_approval?: boolean;
  /** GraphQL flavor (method === 'GRAPHQL'): operation text + kind. */
  graphql_query?: string;
  graphql_operation_type?: GraphQLOperationType;
}

export interface ICreateApiActionResponse {
  is_success: boolean;
  item_id?: string;
  detail?: string;
  message?: string;
  result?: Record<string, unknown>;
}

export interface ITestApiActionPayload {
  parameters: Record<string, unknown>;
  test_config?: Record<string, unknown>;
  project_key: string;
  extraction_only?: boolean;
  is_open_tool?: boolean;
}

export interface ITestApiActionResponse {
  is_success: boolean;
  success?: boolean;
  detail?: string;
  message?: string;
  error?: string;
  result?: Record<string, unknown>;
  response_details?: Record<string, unknown>;
}

export type IDefaultToolDetailsResponse = IGetToolResponse;

export interface IToolLog {
  created_at: string;
  success: boolean;
  execution_time: number;
}

export interface IToolSummary {
  tool_id: string;
  agent_id: string;
  action_id: string;
  total_calls: number;
  successful_calls: number;
  failed_calls: number;
  logs: IToolLog[];
}

export interface IToolSummaryResponse {
  data: IToolSummary;
  success: boolean;
  message?: string;
  status_code?: number;
}

export interface IToolSummaryPayload {
  tool_id: string;
  project_key: string;
  agent_id?: string | null;
  action_id?: string | null;
  call_type?: string[] | null;
  start_date: string | null;
  end_date: string | null;
}

export interface IGetToolsResponse {
  tools: ITool[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface IGetToolAgentCountResponse {
  count?: number;
  agent_count?: number;
  total_count?: number;
  /** BE may return the count directly as a number or wrapped in an object. */
  data?: number | {
    count?: number;
    agent_count?: number;
    total_count?: number;
  };
}

export interface IGetOpenToolsPayload {
  tool_type?: ToolType | null;
  tool_status?: ToolStatus | null;
  page: number;
  page_size: number;
  project_key?: string;
}

export interface IAddMCPServerPayload {
  name: string;
  description: string;
  project_key?: string;
  visibility?: 'private' | 'public';
  is_open_tool?: boolean;
  is_blocks_authenticated?: boolean;
  /** HIL override: null = auto-classify, true = always ask, false = never. */
  requires_approval?: boolean | null;
}

/** A pasted Claude-style {"mcpServers": {...}} block. */
export interface IImportMcpServersPayload {
  content: string;
  project_key?: string;
  visibility?: 'private' | 'public';
  is_open_tool?: boolean;
  status?: ToolStatus;
  /** HIL override: null = auto-classify, true = always ask, false = never. */
  requires_approval?: boolean | null;
}

export interface IImportMcpServerEntry {
  name: string;
  status: 'created' | 'rejected';
  tool_id?: string;
  server_url?: string;
  reason?: string;
}

export interface IImportMcpServersResponse {
  created: number;
  rejected: number;
  results: IImportMcpServerEntry[];
  /** Present only when the whole paste was refused. */
  is_success?: boolean;
  detail?: string;
  message?: string;
}

export interface IAddAPIToolPayload {
  name: string;
  description: string;
  base_url: string;
  is_open_tool: boolean;
  is_blocks_authenticated?: boolean;
  project_key?: string;
  visibility?: 'private' | 'public';
  /** HIL override: null = auto-classify by method, true = always ask, false = never. */
  requires_approval?: boolean | null;
}

export interface IAddToolResponse {
  is_success: boolean;
  item_id?: string;
  detail?: string;
  message?: string;
  data?: ITool;
}

export interface ISaveComposioToolPayload {
  id?: string;
  name: string;
  description: string;
  toolkits: string[];
  tools?: string[] | null;
  limit?: number | null;
  auth_config_ids?: Record<string, string> | null;
  project_key?: string;
  visibility?: 'private' | 'public';
  is_open_tool?: boolean;
  status?: ToolStatus;
  /** HIL override: null = auto-classify, true = always ask, false = never. */
  requires_approval?: boolean | null;
}

export interface ISaveOpenConnectorToolPayload {
  id?: string;
  name: string;
  description: string;
  services?: string[];
  actions?: string[] | null;
  limit?: number | null;
  project_key?: string;
  visibility?: 'private' | 'public';
  is_open_tool?: boolean;
  status?: ToolStatus;
  /** HIL override: null = auto-classify, true = always ask, false = never. */
  requires_approval?: boolean | null;
}

export interface IOpenConnectorCatalogService {
  service: string;
  displayName: string;
  iconUrl: string | null;
  homepageUrl?: string;
  categories?: Array<{ id: string; displayName: string }>;
  authTypes?: string[];
}

export interface IGetOpenConnectorCatalogResponse {
  services: IOpenConnectorCatalogService[];
}

export interface IOpenConnectorAction {
  id: string;
  service: string;
  name: string;
  description?: string;
  requiredScopes?: string[];
  inputSchema?: Record<string, unknown>;
  execution?: {
    locallyExecutable?: boolean;
    needsCredential?: boolean;
    noAuthRunnable?: boolean;
  };
}

export interface IGetOpenConnectorServiceActionsResponse {
  actions: IOpenConnectorAction[];
}

export interface IOpenConnectorConnectionEntry {
  service: string;
  connected: boolean;
  status: string;
  connectionId: string;
  accountLabel: string;
  scopes: string[];
}

export interface IGetOpenConnectorConnectionsResponse {
  connections: Record<string, IOpenConnectorConnectionEntry>;
}

export interface IOpenConnectorInitiateResponse {
  redirectUrl: string;
  service: string;
}

export interface IOpenConnectorDisconnectResponse {
  disconnected: boolean;
}

export type ComposioConnectionStatus =
  | 'NONE'
  | 'INITIALIZING'
  | 'INITIATED'
  | 'ACTIVE'
  | 'EXPIRED'
  | 'FAILED'
  | 'INACTIVE';

export interface IComposioConnectionEntry {
  status: ComposioConnectionStatus;
  connected: boolean;
  connectedAccountId: string | null;
}

export interface IGetComposioConnectionsResponse {
  connections: Record<string, IComposioConnectionEntry>;
}

export interface IComposioInitiateResponse {
  redirectUrl: string;
  toolkit: string;
}

export interface IComposioDisconnectResponse {
  removed: number;
}

export interface IComposioToolkitAvailability {
  toolkit: string;
  authConfigAvailable: boolean;
  authConfigId?: string;
  reason?: string;
}

export interface IGetComposioToolkitsResponse {
  toolkits: IComposioToolkitAvailability[];
}

export interface IComposioToolkitSlug {
  slug: string;
  name?: string | null;
  description?: string | null;
}

export interface IComposioCatalogToolkit {
  slug: string;
  name: string;
  description?: string | null;
  logo?: string | null;
  categories?: string[];
  toolsCount?: number;
  composioManagedAuth: boolean;
  noAuth: boolean;
}

export interface IGetComposioCatalogResponse {
  toolkits: IComposioCatalogToolkit[];
}

export interface IComposioOauthCallbackResponse {
  ok: boolean;
  html?: string | null;
  detail?: string | null;
  toolId?: string | null;
  toolkit?: string | null;
}

export interface IDeleteToolResponse {
  is_success: boolean;
  detail?: string;
  message?: string;
}

/** A standing "Always allow" grant. `actionName === '*'` covers the whole tool. */
export interface IApprovalGrant {
  id: string;
  toolId: string;
  toolName: string;
  actionName: string;
  createdDate: string;
  conversationId?: string;
}

/** A grant scoped to one conversation ("Allow for this chat"). Keyed by tool + action. */
export interface IChatApprovalGrant {
  toolId: string;
  toolName: string;
  actionName: string;
  grantedDate: string;
}

/* ---- v2: Swagger import + per-user (token-only) connection + write confirm ---- */

export interface IImportOpenapiPayload {
  specUrl?: string;
  specContent?: string;
}

export interface IImportGraphqlPayload {
  /** 'file' uploads/pastes SDL; 'url' fetches a public SDL or introspection JSON document. */
  source: 'file' | 'url';
  sdl?: string;
  schemaUrl?: string;
  graphqlUrl?: string;
  /** Nested-field expansion depth for generated operations (default 2, min 1). */
  depth?: number;
}

export interface IIngestStatusResponse {
  status?: string;
}

export interface IToolConnection {
  connected: boolean;
  hasRefresh?: boolean;
  /** API tools whose sign-in names a client: the owner lets users bring their own. */
  userClientAllowed?: boolean;
  /** Whether the tool has its own client configured. */
  toolClientSet?: boolean;
  /** The caller's own client ID, masked; absent when they saved none (or it is not allowed). */
  ownClientId?: string | null;
  ownClientSecretSaved?: boolean;
}

export interface IConnectToolPayload {
  username: string;
  password: string;
}

export interface IConnectToolResponse {
  connected: boolean;
  error?: string;
}

/** The user's own credentials for a tool; only the fields its sign-in type uses are sent. */
export interface ISaveToolCredentialsPayload {
  apiToken?: string;
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
}

export interface ISaveToolCredentialsResponse {
  saved: boolean;
  error?: string;
}

export interface IStartToolOidcResponse {
  authorizationUrl: string;
}

export class GptChatService {
  async getAgents(payload: IGetAgentsModelPayload, blocksKey: string): Promise<IGetAgentsResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsQueries}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }
  async getllmModels(blocksKey: string): Promise<IGetAgentModelResponse> {
    const config = await getConfigurationSettings();
    return clients.get(`${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsModels}`, blocksKey);
  }

  async getTools(payload: IGetToolsPayload, blocksKey: string): Promise<IGetToolsResponse> {
    const {
      tool_type,
      tool_status,
      page = 1,
      page_size = 50,
      project_key,
      search,
      created_by,
      with_system_tool,
    } = payload;

    const params = new URLSearchParams({
      page: String(page),
      page_size: String(page_size),
    });

    if (tool_type) {
      params.append('tool_type', tool_type);
    }

    if (tool_status) {
      params.append('tool_status', tool_status);
    }

    if (project_key) {
      params.append('project_key', project_key);
    }

    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      params.append('search', trimmedSearch);
    }

    if (created_by) {
      params.append('created_by', created_by);
    }

    if (with_system_tool !== undefined) {
      params.append('with_system_tool', String(with_system_tool));
    }

    const config = await getConfigurationSettings();
    return clients.get(`${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}?${params.toString()}`, blocksKey);
  }

  async getToolAgentCount(
    toolId: string,
    projectKey: string,
    blocksKey: string
  ): Promise<IGetToolAgentCountResponse> {
    const params = new URLSearchParams();

    if (projectKey) {
      params.append('project_key', projectKey);
    }

    const config = await getConfigurationSettings();
    const query = params.toString();

    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolAgentCount}/${toolId}/agent-count${query ? `?${query}` : ''}`,
      blocksKey
    );
  }

  async getToolById(toolId: string, projectKey: string, blocksKey: string): Promise<IGetToolResponse> {
    const params = new URLSearchParams();

    if (projectKey) {
      params.append('project_key', projectKey);
    }

    const config = await getConfigurationSettings();
    const query = params.toString();

    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}${query ? `?${query}` : ''}`,
      blocksKey
    );
  }

  async getOpenToolDetails(toolId: string, blocksKey: string): Promise<IDefaultToolDetailsResponse> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolDefaults}/${toolId}`,
      blocksKey
    );
  }

  async getToolSchema(
    toolId: string,
    projectKey: string,
    blocksKey: string,
    isBlocksTool?: boolean
  ): Promise<IGetToolSchemaResponse> {
    const params = new URLSearchParams({
      project_key: projectKey,
    });

    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolSchemaOpenApi}/${toolId}/schema/openapi?${params.toString()}`,
      blocksKey
    );
  }

  async getToolSummary(payload: IToolSummaryPayload, blocksKey: string): Promise<IToolSummaryResponse> {
    const params = new URLSearchParams({
      project_key: payload.project_key,
      start_date: payload.start_date ?? '',
      end_date: payload.end_date ?? '',
    });

    if (payload.agent_id) {
      params.append('agent_id', payload.agent_id);
    }

    if (payload.action_id) {
      params.append('action_id', payload.action_id);
    }

    (payload.call_type ?? []).forEach((callType) => {
      params.append('call_type', callType);
    });

    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolSummary}/${payload.tool_id}/summary?${params.toString()}`,
      blocksKey
    );
  }

  async updateMCPServer(payload: IUpdateMCPServerPayload, blocksKey: string): Promise<IAddToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolMcpServer}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  /** POST /api/tools/api — create or update an API tool (includes status). */
  async updateAPITool(payload: IUpdateAPIToolPayload, blocksKey: string): Promise<IAddToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolApi}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async saveAuthConfig(
    toolId: string,
    payload: ISaveAuthConfigPayload,
    projectKey: string,
    blocksKey: string
  ): Promise<IAddToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolApiAuthConfig}/${toolId}`,
      blocksKey,
      JSON.stringify({ ...payload, project_key: projectKey })
    );
  }

  async getToolTutorial(
    toolId: string,
    projectKey: string,
    blocksKey: string
  ): Promise<IGetToolTutorialResponse> {
    const params = new URLSearchParams();

    if (projectKey) {
      params.append('project_key', projectKey);
    }

    const config = await getConfigurationSettings();
    const query = params.toString();

    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolTutorial}/${toolId}${query ? `?${query}` : ''}`,
      blocksKey
    );
  }

  async saveTutorial(
    toolId: string,
    payload: ISaveTutorialPayload,
    projectKey: string,
    blocksKey: string
  ): Promise<IAddToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolTutorial}/${toolId}`,
      blocksKey,
      JSON.stringify({
        ...payload,
        id: toolId,
        tool_id: toolId,
        Tutorial: payload.Tutorial ?? payload.tutorial,
        project_key: projectKey,
      })
    );
  }

  async deleteToolApiAction(
    toolId: string,
    actionId: string,
    projectKey: string,
    blocksKey: string,
    isBlocksTool?: boolean
  ): Promise<IDeleteToolResponse> {
    const params = new URLSearchParams();
    if (projectKey) params.append('project_key', projectKey);
    if (typeof isBlocksTool !== 'undefined') params.append('is_open_tool', String(isBlocksTool));

    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolDeleteAction}/${toolId}/action/${actionId}?${params.toString()}`,
      blocksKey
    );
  }

  async deleteAllToolActions(
    toolId: string,
    projectKey: string,
    blocksKey: string,
    isBlocksTool?: boolean
  ): Promise<IDeleteToolResponse> {
    const params = new URLSearchParams();
    if (projectKey) params.append('project_key', projectKey);
    if (typeof isBlocksTool !== 'undefined') params.append('is_open_tool', String(isBlocksTool));

    const config = await getConfigurationSettings();
    const qs = params.toString();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolDeleteAction}/${toolId}/actions${qs ? `?${qs}` : ''}`,
      blocksKey
    );
  }

  /** Turns every action of a tool on or off in one request. */
  async setAllToolActionsEnabled(
    toolId: string,
    enabled: boolean,
    projectKey: string,
    blocksKey: string,
    isBlocksTool?: boolean
  ): Promise<IDeleteToolResponse> {
    const params = new URLSearchParams();
    if (projectKey) params.append('project_key', projectKey);
    if (typeof isBlocksTool !== 'undefined') params.append('is_open_tool', String(isBlocksTool));

    const config = await getConfigurationSettings();
    const qs = params.toString();
    return clients.put(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolDeleteAction}/${toolId}/actions/enabled${qs ? `?${qs}` : ''}`,
      blocksKey,
      JSON.stringify({ enabled })
    );
  }

  async createApiAction(
    toolId: string,
    payload: ICreateApiActionPayload,
    projectKey: string,
    blocksKey: string,
    actionId?: string
  ): Promise<ICreateApiActionResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolApiAction}/${toolId}`,
      blocksKey,
      JSON.stringify({
        ...payload,
        project_key: projectKey,
        ...(actionId ? { actionId } : {}),
      })
    );
  }

  async testApiAction(
    toolId: string,
    actionId: string,
    payload: ITestApiActionPayload,
    blocksKey: string
  ): Promise<ITestApiActionResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolTestAction}/${toolId}/test-action/${actionId}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async setupMCPServer(
    toolId: string,
    payload: IMCPServerSetupPayload,
    blocksKey: string
  ): Promise<IMCPServerSetupResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolMcpServerSetup}/${toolId}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async refreshMCPServer(
    toolId: string,
    projectKey: string,
    blocksKey: string,
    isBlocksTool?: boolean
  ): Promise<IAddToolResponse> {
    const params = new URLSearchParams({ project_key: projectKey });
    if (typeof isBlocksTool !== 'undefined') params.append('is_open_tool', String(isBlocksTool));

    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolRefreshMcp}/${toolId}/refresh-mcp?${params.toString()}`,
      blocksKey,
      JSON.stringify({})
    );
  }

  async testMcpAuth(
    toolId: string,
    projectKey: string,
    payload: { auth_config: Record<string, unknown> },
    blocksKey: string,
    isBlocksTool?: boolean
  ): Promise<IAddToolResponse> {
    const params = new URLSearchParams({ project_key: projectKey });
    if (typeof isBlocksTool !== 'undefined') params.append('is_open_tool', String(isBlocksTool));

    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolTestMcpAuth}/${toolId}/test-mcp-auth?${params.toString()}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async getOpenTools(payload: IGetOpenToolsPayload, blocksKey: string): Promise<IGetToolsResponse> {
    const params = new URLSearchParams();

    if (payload.tool_type) params.append('tool_type', payload.tool_type);
    if (payload.tool_status) params.append('tool_status', payload.tool_status);
    params.append('page', String(payload.page));
    params.append('page_size', String(payload.page_size));
    if (payload.project_key) params.append('project_key', payload.project_key);

    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolsDefaultsList}?${params.toString()}`,
      blocksKey
    );
  }

  async addMCPServer(
    payload: IAddMCPServerPayload,
    blocksKey: string
  ): Promise<IAddToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolMcpServer}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  /** POST /tools/mcp-server/import — Claude-style mcpServers block, per-entry outcomes. */
  async importMcpServers(
    payload: IImportMcpServersPayload,
    blocksKey: string
  ): Promise<IImportMcpServersResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolMcpServerImport}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async addAPITool(
    payload: IAddAPIToolPayload,
    blocksKey: string
  ): Promise<IAddToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolApi}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async saveComposioTool(
    payload: ISaveComposioToolPayload,
    blocksKey: string
  ): Promise<IAddToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolComposio}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async saveOpenConnectorTool(
    payload: ISaveOpenConnectorToolPayload,
    blocksKey: string
  ): Promise<IAddToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolOpenConnector}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async getOpenConnectorCatalog(
    blocksKey: string,
    search?: string,
    limit = 30
  ): Promise<IGetOpenConnectorCatalogResponse> {
    const config = await getConfigurationSettings();
    const params = new URLSearchParams({ limit: String(limit) });
    if (search) params.append('search', search);
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolOpenConnectorCatalog}?${params.toString()}`,
      blocksKey
    );
  }

  async getOpenConnectorServiceActions(
    service: string,
    blocksKey: string
  ): Promise<IGetOpenConnectorServiceActionsResponse> {
    const config = await getConfigurationSettings();
    const path = config.paths.seliseAi.toolOpenConnectorServiceActions.replace(
      ':service',
      encodeURIComponent(service)
    );
    return clients.get(`${config.paths.seliseAiBaseUrl}${path}`, blocksKey);
  }

  private openConnectorConnectionsPath(
    paths: { toolOpenConnectorConnections: string },
    toolId: string
  ): string {
    return paths.toolOpenConnectorConnections.replace(':toolId', encodeURIComponent(toolId));
  }

  async getOpenConnectorConnections(
    toolId: string,
    blocksKey: string
  ): Promise<IGetOpenConnectorConnectionsResponse> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${this.openConnectorConnectionsPath(config.paths.seliseAi, toolId)}`,
      blocksKey
    );
  }

  async initiateOpenConnectorConnection(
    toolId: string,
    service: string,
    blocksKey: string
  ): Promise<IOpenConnectorInitiateResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${this.openConnectorConnectionsPath(config.paths.seliseAi, toolId)}/${encodeURIComponent(service)}/initiate`,
      blocksKey,
      JSON.stringify({})
    );
  }

  async disconnectOpenConnectorConnection(
    toolId: string,
    service: string,
    blocksKey: string
  ): Promise<IOpenConnectorDisconnectResponse> {
    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${this.openConnectorConnectionsPath(config.paths.seliseAi, toolId)}/${encodeURIComponent(service)}`,
      blocksKey
    );
  }

  private composioConnectionsPath(paths: { toolComposioConnections: string }, toolId: string): string {
    return paths.toolComposioConnections.replace(':toolId', encodeURIComponent(toolId));
  }

  async getComposioConnections(
    toolId: string,
    blocksKey: string
  ): Promise<IGetComposioConnectionsResponse> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${this.composioConnectionsPath(config.paths.seliseAi, toolId)}`,
      blocksKey
    );
  }

  async initiateComposioConnection(
    toolId: string,
    toolkit: string,
    blocksKey: string
  ): Promise<IComposioInitiateResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${this.composioConnectionsPath(config.paths.seliseAi, toolId)}/${encodeURIComponent(toolkit)}/initiate`,
      blocksKey,
      JSON.stringify({})
    );
  }

  async disconnectComposioConnection(
    toolId: string,
    toolkit: string,
    blocksKey: string
  ): Promise<IComposioDisconnectResponse> {
    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${this.composioConnectionsPath(config.paths.seliseAi, toolId)}/${encodeURIComponent(toolkit)}`,
      blocksKey
    );
  }

  async getComposioToolkits(
    blocksKey: string,
    toolId?: string
  ): Promise<IGetComposioToolkitsResponse> {
    const config = await getConfigurationSettings();
    const query = toolId ? `?tool_id=${encodeURIComponent(toolId)}` : '';
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolComposioToolkits}${query}`,
      blocksKey
    );
  }

  async getComposioToolkitCatalog(
    blocksKey: string,
    search?: string,
    limit = 1000
  ): Promise<IGetComposioCatalogResponse> {
    const config = await getConfigurationSettings();
    const params = new URLSearchParams({ limit: String(limit) });
    if (search) params.append('search', search);
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolComposioCatalog}?${params.toString()}`,
      blocksKey
    );
  }

  async getComposioToolkitSlugs(
    toolkit: string,
    blocksKey: string
  ): Promise<IComposioToolkitSlug[]> {
    const config = await getConfigurationSettings();
    const path = config.paths.seliseAi.toolComposioToolkitTools.replace(
      ':toolkit',
      encodeURIComponent(toolkit)
    );
    const response = await clients.get<{ tools?: IComposioToolkitSlug[] } | IComposioToolkitSlug[]>(
      `${config.paths.seliseAiBaseUrl}${path}`,
      blocksKey
    );
    return Array.isArray(response) ? response : (response?.tools ?? []);
  }

  private parseComposioOauthHtmlPayload(html: string): {
    toolId?: string | null;
    toolkit?: string | null;
  } {
    const messageMatch = /postMessage\(\s*(\{[\s\S]*?\})\s*,/.exec(html);
    if (!messageMatch?.[1]) {
      return {};
    }

    try {
      const normalized = messageMatch[1]
        .replace(/(['"])?([a-zA-Z0-9_]+)\1\s*:/g, '"$2":')
        .replace(/'/g, '"');
      const embedded = JSON.parse(normalized) as {
        toolId?: string;
        toolkit?: string;
      };
      return {
        toolId: embedded.toolId ?? null,
        toolkit: embedded.toolkit ?? null,
      };
    } catch {
      return {};
    }
  }

  async completeComposioOauthCallback(
    query: Record<string, string>
  ): Promise<IComposioOauthCallbackResponse> {
    const config = await getConfigurationSettings();
    const params = new URLSearchParams(query);
    const url = `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolComposioOauthCallback}?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json, text/html;q=0.9,*/*;q=0.8',
      },
    });

    const contentType = response.headers.get('Content-Type') || '';
    const raw = await response.text();
    const trimmed = raw.trim();

    if (contentType.includes('application/json') || trimmed.startsWith('{')) {
      let parsed: {
        is_success?: boolean;
        ok?: boolean;
        message?: string;
        detail?: string;
        errors?: { message?: string } | string;
      };

      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return { ok: false, detail: 'Invalid OAuth callback response.' };
      }

      const errorMessage =
        (typeof parsed.errors === 'object' && parsed.errors?.message) ||
        (typeof parsed.errors === 'string' ? parsed.errors : null) ||
        parsed.detail ||
        parsed.message ||
        'The authorization did not complete.';

      return { ok: false, detail: errorMessage };
    }

    if (!response.ok) {
      return {
        ok: false,
        detail: `OAuth callback failed (${response.status}).`,
      };
    }

    const embedded = this.parseComposioOauthHtmlPayload(raw);

    return {
      ok: true,
      html: raw,
      toolId: embedded.toolId ?? null,
      toolkit: embedded.toolkit ?? null,
    };
  }

  async copyToolFromDefault(
    defaultToolId: string,
    projectKey: string,
    blocksKey: string
  ): Promise<IAddToolResponse> {
    const params = new URLSearchParams({
      project_key: projectKey,
    });

    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolCopyFromDefault}/${defaultToolId}?${params.toString()}`,
      blocksKey,
      JSON.stringify({})
    );
  }

  async deleteTool(
    toolId: string,
    projectKey: string,
    blocksKey: string,
    isBlocksTool?: boolean
  ): Promise<IDeleteToolResponse> {
    const params = new URLSearchParams({
      project_key: projectKey,
    });

    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}?${params.toString()}`,
      blocksKey
    );
  }

  /** POST /api/tools/{toolId}/import-openapi — async Swagger ingest via worker. */
  async importOpenapi(
    toolId: string,
    payload: IImportOpenapiPayload,
    blocksKey: string
  ): Promise<IIngestStatusResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/import-openapi`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  /** POST /api/tools/{toolId}/import-graphql — async GraphQL ingest; reports via the same MCP_INGEST notification as Swagger. */
  async importGraphql(
    toolId: string,
    payload: IImportGraphqlPayload,
    blocksKey: string
  ): Promise<IIngestStatusResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/import-graphql`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  /** POST /api/tools/{toolId}/resync — re-ingest the previously imported spec. */
  async resyncTool(toolId: string, blocksKey: string): Promise<IIngestStatusResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/resync`,
      blocksKey,
      JSON.stringify({})
    );
  }

  /** GET /api/tools/{toolId}/connection — per-user token-cache status. */
  async getToolConnection(toolId: string, blocksKey: string): Promise<IToolConnection> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/connection`,
      blocksKey
    );
  }

  /** POST /api/tools/{toolId}/connect — mint + cache token from credentials. */
  async connectTool(
    toolId: string,
    payload: IConnectToolPayload,
    blocksKey: string
  ): Promise<IConnectToolResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/connect`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  /** POST /api/tools/{toolId}/credentials — save the caller's own credentials for the tool. */
  async saveToolCredentials(
    toolId: string,
    payload: ISaveToolCredentialsPayload,
    blocksKey: string
  ): Promise<ISaveToolCredentialsResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/credentials`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  /** DELETE /api/tools/{toolId}/credentials/client — remove the caller's own client and sign them out. */
  async deleteOwnToolClient(toolId: string, blocksKey: string): Promise<void> {
    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/credentials/client`,
      blocksKey
    );
  }

  /** DELETE /api/tools/{toolId}/connection — clear cached token. */
  async disconnectTool(toolId: string, blocksKey: string): Promise<void> {
    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/connection`,
      blocksKey
    );
  }

  /** GET /api/tools/{toolId}/oauth/start — build the IdP authorization URL. */
  async startToolOidc(
    toolId: string,
    blocksKey: string,
    conversationId?: string
  ): Promise<IStartToolOidcResponse> {
    const params = new URLSearchParams();
    if (conversationId) params.append('conversationId', conversationId);
    const query = params.toString();

    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.tools}/${toolId}/oauth/start${query ? `?${query}` : ''}`,
      blocksKey
    );
  }

  /** GET /api/tools/approval-grants — the caller's standing HIL approval grants. */
  async getApprovalGrants(blocksKey: string): Promise<IApprovalGrant[]> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolApprovalGrants}`,
      blocksKey
    );
  }

  /** DELETE /api/tools/approval-grants/{grantId} — revoke one standing grant. */
  async revokeApprovalGrant(grantId: string, blocksKey: string): Promise<IDeleteToolResponse> {
    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolApprovalGrants}/${grantId}`,
      blocksKey
    );
  }

  /** GET /api/tools/grants/chat/{conversationId} — grants scoped to one conversation. */
  async getChatApprovalGrants(
    conversationId: string,
    blocksKey: string
  ): Promise<IChatApprovalGrant[]> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolChatApprovalGrants}/${encodeURIComponent(conversationId)}`,
      blocksKey
    );
  }

  /** DELETE /api/tools/grants/chat/{conversationId}?toolId=&actionName= — revoke one chat grant. */
  async revokeChatApprovalGrant(
    conversationId: string,
    toolId: string,
    actionName: string,
    blocksKey: string
  ): Promise<IDeleteToolResponse> {
    const params = new URLSearchParams({ toolId, actionName });
    const config = await getConfigurationSettings();
    return clients.delete(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.toolChatApprovalGrants}/${encodeURIComponent(conversationId)}?${params.toString()}`,
      blocksKey
    );
  }
}

export const gptChatService = new GptChatService();
